import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableVirtuoso, type TableComponents } from 'react-virtuoso'
import { type SettingLogHistoryItem, useGetSettingLogHistoryQuery } from '../../store/api/settingsApi'
import '../../styles/app-shared.css'

/* ---------- helpers -------------------------------------------------- */

const todayStr = () => new Date().toISOString().slice(0, 10)

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`.slice(-2)
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  const ss = `${d.getSeconds()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}:${ss}`
}

/* ---------- style constants (DocStyle §16) ----------------------------- */

const headerButtonSx = {
  minHeight: 28,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const filterBarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none' as const,
  fontSize: '0.7rem',
  lineHeight: 1.1,
}

/* ---------- column definitions (Access §2.3 order) --------------------- */

interface ColDef {
  key: keyof SettingLogHistoryItem
  label: string
  minWidth: number
  align?: 'left' | 'right' | 'center'
  multiline?: boolean
}

const ALL_COLUMNS: ColDef[] = [
  { key: 'tableTxt',      label: 'Table name',     minWidth: 140 },
  { key: 'personUpdated', label: 'Person updated',  minWidth: 200 },
  { key: 'dateUpdated',   label: 'Date updated',    minWidth: 148 },
  { key: 'rowUpdated',    label: 'Record',          minWidth: 280, multiline: true },
  { key: 'snapshotDate',  label: 'Date',            minWidth: 100 },
  { key: 'clientId',      label: 'Client ID',       minWidth: 100 },
  { key: 'schemaTxt',     label: 'Schema',          minWidth: 100 },
]

const LS_KEY = 'settings_log_hidden_cols'

const loadHiddenCols = (): Set<string> => {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return new Set(JSON.parse(raw) as string[])
  } catch { /* ignore */ }
  return new Set()
}

const saveHiddenCols = (hidden: Set<string>) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...hidden]))
  } catch { /* ignore */ }
}

/* ---------- Virtuoso table components --------------------------------- */

const VirtuosoTableComponents: TableComponents<SettingLogHistoryItem> = {
  Scroller: ({ children, ...props }) => (
    <Box
      component="div"
      {...props}
      sx={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}
    >
      {children}
    </Box>
  ),
  Table: ({ children, style }) => (
    <Table
      className="app-table-wrap"
      size="small"
      stickyHeader
      style={{ ...style, borderCollapse: 'separate', tableLayout: 'auto' }}
    >
      {children}
    </Table>
  ),
  TableHead: ({ children, ...props }) => <TableHead {...props}>{children}</TableHead>,
  TableRow: ({ children, ...props }) => <TableRow hover {...props}>{children}</TableRow>,
  TableBody: ({ children, ...props }) => <TableBody {...props}>{children}</TableBody>,
}

/* ---------- component ------------------------------------------------- */

export default function SettingsLogHistoryView() {
  const [dateStr, setDateStr] = useState(todayStr)
  const [fetchDate, setFetchDate] = useState(todayStr)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(loadHiddenCols)

  const { data, isLoading, isFetching, error, refetch } = useGetSettingLogHistoryQuery(fetchDate)

  const rows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((row) =>
      ALL_COLUMNS.some((col) => {
        const value = row[col.key]
        if (value == null) return false
        return String(value).toLowerCase().includes(needle)
      }),
    )
  }, [rows, searchTerm])

  const visibleCols = useMemo(
    () => ALL_COLUMNS.filter((c) => !hiddenCols.has(c.key)),
    [hiddenCols],
  )

  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])

  const handleDateChange = (v: string) => {
    setDateStr(v)
    setFetchDate(v)
    setPage(0)
  }

  const handleRefresh = () => {
    setFetchDate(dateStr)
    setPage(0)
    refetch()
  }

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const toggleCol = useCallback((key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      saveHiddenCols(next)
      return next
    })
  }, [])

  const renderCell = (row: SettingLogHistoryItem, col: ColDef) => {
    const raw = row[col.key]
    if (col.key === 'dateUpdated') {
      const val = formatAccessDate(raw)
      return (
        <TableCell key={col.key} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
          {val}
        </TableCell>
      )
    }
    if (col.multiline && raw) {
      return (
        <TableCell key={col.key} sx={{ py: 0.5, maxWidth: 360 }}>
          <Box
            sx={{
              maxHeight: 72,
              overflowY: 'auto',
              fontSize: '0.72rem',
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {raw}
          </Box>
        </TableCell>
      )
    }
    const val = raw ?? '-'
    return (
      <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
        <Box
          component="span"
          className="app-table-cell-text"
          title={String(val)}
          sx={{ display: 'inline-block', width: '100%' }}
        >
          {val || '-'}
        </Box>
      </TableCell>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* ── Header row: column toggles (left) + date picker + Refresh + Search (right) */}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        {/* Column visibility toggles */}
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center">
          {ALL_COLUMNS.map((col) => (
            <Button
              key={col.key}
              size="small"
              variant={hiddenCols.has(col.key) ? 'outlined' : 'contained'}
              color={hiddenCols.has(col.key) ? 'inherit' : 'primary'}
              onClick={() => toggleCol(col.key)}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0.2,
                minHeight: 26,
                textTransform: 'none',
                fontSize: '0.68rem',
                lineHeight: 1.1,
              }}
            >
              {col.label}
            </Button>
          ))}
        </Stack>

        {/* Right controls: date + refresh + search */}
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
            Snapshot Date:
          </Typography>
          <TextField
            size="small"
            type="date"
            value={dateStr}
            onChange={(e) => handleDateChange(e.target.value)}
            sx={{ width: 150 }}
            inputProps={{ style: { fontSize: '0.8rem' } }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleRefresh}
            disabled={isFetching}
            startIcon={isFetching ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: 14 }} />}
            sx={headerButtonSx}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setSearchAnchorEl(e.currentTarget)}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
        </Stack>
      </Stack>

      <Popover
        open={searchAnchorEl !== null}
        anchorEl={searchAnchorEl}
        onClose={() => setSearchAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 320 } }}
      >
        <Stack spacing={1}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Search log history"
            placeholder="Table, person, record..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={() => {
              setSearchTerm('')
              setSearchAnchorEl(null)
            }}
          >
            Clear Search
          </Button>
        </Stack>
      </Popover>

      <Divider sx={{ mb: 0.5 }} />

      {/* ── Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
          Failed to load log history.
        </Alert>
      )}

      {/* ── Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* ── Data grid */}
      {!isLoading && (
        <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0 }}>
          {/* Pagination toolbar */}
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
              minHeight: 30,
              py: 0.125,
            }}
          >
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={{
                flexShrink: 0,
                '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
              }}
            />
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
            <TableVirtuoso
              data={pagedRows}
              components={VirtuosoTableComponents}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default', py: 0.5, width: 40, minWidth: 40, fontSize: '0.72rem' }}
                  >
                    #
                  </TableCell>
                  {visibleCols.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default', py: 0.5, minWidth: col.minWidth }}
                    >
                      <Box className="app-table-head-text">{col.label}</Box>
                    </TableCell>
                  ))}
                </TableRow>
              )}
              itemContent={(index, row) => (
                <>
                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, color: 'text.secondary', fontSize: '0.68rem' }}>
                    {(rowsPerPage === -1 ? 0 : page * rowsPerPage) + index + 1}
                  </TableCell>
                  {visibleCols.map((col) => renderCell(row, col))}
                </>
              )}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
